/**
 * Loading, empty and failed, designed rather than defaulted.
 *
 * These replace four near-identical hand-rolled red error panels and two
 * byte-identical empty states that had been copied between the run table and the
 * board.
 *
 * The distinction the whole file turns on: **not-yet-loaded, loaded-and-empty and
 * failed are three states, not one.** This UI reads a leaderboard where "no rows"
 * is a finding — a sweep can run to completion and rank nothing — so an empty table
 * that looks like a table still loading, or like a request that 500'd, destroys the
 * one thing the reader came for. The tables that already keep `loaded === false`
 * separate from `total === 0` are doing it on purpose; nothing here may collapse them.
 */
import type { ReactNode } from 'react'
import { AlertTriangle } from 'lucide-react'
import { Button } from './Button'
import { cn } from '../../lib/utils'

/**
 * Row-shaped placeholders while a request is in flight.
 *
 * Shaped like the content rather than a spinner, so the layout does not jump when
 * the rows arrive, and `aria-busy` with a live label so a screen reader is told the
 * table is loading instead of being read an empty table.
 */
export function LoadingSkeleton({
  rows = 6,
  className,
}: {
  rows?: number
  className?: string
}) {
  return (
    <div
      className={cn('space-y-2 p-5', className)}
      aria-busy="true"
      aria-live="polite"
      aria-label="Loading"
    >
      {Array.from({ length: rows }).map((_, i) => (
        <div
          key={i}
          className="h-9 animate-pulse rounded-lg bg-slate-800"
          // Staggered, so it reads as a list filling in rather than one block
          // flashing. Cut to nothing by the reduced-motion rule in index.css.
          style={{ animationDelay: `${i * 80}ms` }}
        />
      ))}
    </div>
  )
}

/**
 * Loaded, and there is genuinely nothing.
 *
 * `message` says what was searched and found nothing, not just "no data" — the
 * difference between "this sweep ranked no targets" and "no sweep has run" is a
 * finding in one case and a setup step in the other, and only the caller knows which.
 */
export function EmptyState({
  message,
  hint,
  action,
}: {
  message: string
  hint?: string
  action?: ReactNode
}) {
  return (
    <div className="px-5 py-12 text-center">
      <p className="text-sm text-slate-300">{message}</p>
      {hint && (
        <p className="mx-auto mt-1.5 max-w-md text-xs leading-relaxed text-slate-500">
          {hint}
        </p>
      )}
      {action && <div className="mt-4 flex justify-center">{action}</div>}
    </div>
  )
}

/**
 * A request failed.
 *
 * The message is rendered verbatim, never summarised to "Something went wrong":
 * these are API errors carrying a status and a reason, and the reason is what tells
 * a reader whether to retry, reconnect to the VPN or report a bug. It stays
 * selectable so it can be pasted into one.
 */
export function FailedState({
  message,
  onRetry,
  className,
}: {
  message: string
  onRetry?: () => void
  className?: string
}) {
  return (
    <div
      role="alert"
      className={cn(
        'flex items-start gap-3 rounded-xl border border-red-900/60 bg-red-950/40 px-4 py-3',
        className,
      )}
    >
      <AlertTriangle
        className="mt-0.5 h-4 w-4 shrink-0 text-red-400"
        aria-hidden="true"
      />
      <div className="min-w-0 flex-1">
        <p className="text-sm font-medium text-red-300">Request failed</p>
        <p className="mt-0.5 break-words text-xs leading-relaxed text-red-300/90">
          {message}
        </p>
      </div>
      {onRetry && (
        <Button variant="outline" size="sm" onClick={onRetry} className="shrink-0">
          Retry
        </Button>
      )}
    </div>
  )
}
